import React, { useMemo, useState } from 'react';
import {
  BellIcon,
  BoltIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  SignalIcon,
  UserCircleIcon,
  UserGroupIcon,
  UsersIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import ProviderManagement from '../components/ProviderManagement';
import ShopAdminManagement from '../components/ShopAdminManagement';
import GlobalStats from '../components/GlobalStats';
import Monitoring from './Monitoring';
import SystemNotifications from './SystemNotifications';
import Shops from './Shops';
import AllUsers from './AllUsers';
import { ThemeToggle } from '../components/ui';
import { classNames } from '../utils/classNames';

 type OwnerTab = 'stats' | 'providers' | 'shop-admins' | 'shops' | 'all-users' | 'monitoring' | 'notifications';

type OwnerTabConfig = {
  id: OwnerTab;
  name: string;
  icon: React.ReactNode;
  description: string;
};

const OwnerDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<OwnerTab>('stats');

  if (user?.role !== 'owner') {
    return <div className="flex min-h-screen items-center justify-center bg-[var(--app-bg)] text-danger">Доступ запрещен</div>;
  }

  const tabs = useMemo<OwnerTabConfig[]>(
    () => [
      {
        id: 'stats',
        name: 'Статистика',
        icon: <ChartBarIcon className="h-4 w-4" />, 
        description: 'Глобальная статистика по магазинам, провайдерам и ключевым метрикам платформы.',
      },
      {
        id: 'providers',
        name: 'Провайдеры',
        icon: <BoltIcon className="h-4 w-4" />,
        description: 'Управляйте платежными провайдерами и их параметрами интеграции.',
      },
      {
        id: 'shops',
        name: 'Магазины',
        icon: <BuildingStorefrontIcon className="h-4 w-4" />,
        description: 'Создавайте магазины, настраивайте параметры и отслеживайте активность.',
      },
      {
        id: 'shop-admins',
        name: 'Администраторы магазинов',
        icon: <UsersIcon className="h-4 w-4" />,
        description: 'Назначайте и управляйте администраторами для каждого магазина.',
      },
      {
        id: 'all-users',
        name: 'Все пользователи',
        icon: <UserGroupIcon className="h-4 w-4" />,
        description: 'Полный список пользователей платформы и их роли.',
      },
      {
        id: 'monitoring',
        name: 'Мониторинг',
        icon: <SignalIcon className="h-4 w-4" />,
        description: 'Анализ инцидентов, неуспешных входов и прочих системных событий.',
      },
      {
        id: 'notifications',
        name: 'Уведомления',
        icon: <BellIcon className="h-4 w-4" />,
        description: 'Редактируйте глобальные шаблоны уведомлений и каналы доставки.',
      },
    ],
    []
  );

  const activeTabConfig = useMemo(() => tabs.find((tab) => tab.id === activeTab), [tabs, activeTab]);

  const initials = useMemo(() => {
    if (!user?.username) return 'GA';
    const parts = user.username
      .trim()
      .split(/[\s._-]+/)
      .filter(Boolean);
    const [first, second] = parts;
    const base = `${first?.[0] ?? ''}${second?.[0] ?? ''}`.toUpperCase();
    return base || user.username.slice(0, 2).toUpperCase();
  }, [user?.username]);

  const roleLabel = 'Глобальный администратор';

  const renderTabContent = () => {
    switch (activeTab) {
      case 'stats':
        return <GlobalStats />;
      case 'providers':
        return <ProviderManagement />;
      case 'shops':
        return <Shops />;
      case 'shop-admins':
        return <ShopAdminManagement />;
      case 'all-users':
        return <AllUsers />;
      case 'monitoring':
        return <Monitoring />;
      case 'notifications':
        return <SystemNotifications />;
      default:
        return <GlobalStats />;
    }
  };

  return (
    <div className="min-h-screen bg-[var(--app-bg)] text-[var(--app-text)]">
      <header className="border-b border-[var(--app-border)] bg-[var(--app-surface)]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-4 px-4 py-5 sm:px-6 lg:px-8">
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Глобальное управление</p>
            <h1 className="text-2xl font-semibold text-[var(--app-heading)]">Панель глобального администратора</h1>
            <p className="text-sm text-[var(--app-text-muted)]">
              Консолидированное управление магазинами, провайдерами и пользователями платформы.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle size="sm" className="hidden sm:inline-flex" />
            <div className="flex items-center gap-3 rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-1.5 shadow-sm">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-white">
                {initials}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[var(--app-heading)]">{user.username}</p>
                <p className="text-xs text-[var(--app-text-muted)]">{roleLabel}</p>
              </div>
              <button
                onClick={logout}
                className="inline-flex items-center gap-1 rounded-full bg-[var(--app-surface-subtle)] px-2.5 py-1 text-xs font-semibold text-danger transition hover:text-danger/80"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
                Выйти
              </button>
            </div>
          </div>
        </div>
      </header>

      <nav className="border-b border-[var(--app-border)] bg-[var(--app-surface)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto py-3">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={classNames(
                  'inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--app-ring-offset)]',
                  activeTab === tab.id
                    ? 'border-primary-200 bg-primary-50 text-primary-600 shadow-sm'
                    : 'border-[var(--app-border)] bg-[var(--app-surface)] text-[var(--app-text-muted)] hover:border-primary-200 hover:text-primary-600'
                )}
              >
                <span className="flex h-5 w-5 items-center justify-center text-sm">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {activeTabConfig?.description ? (
        <div className="border-b border-[var(--app-border)] bg-[var(--app-surface-subtle)]">
          <div className="mx-auto max-w-7xl px-4 py-3 text-sm text-[var(--app-text-muted)] sm:px-6 lg:px-8">
            {activeTabConfig.description}
          </div>
        </div>
      ) : null}

      <main className="py-6">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">{renderTabContent()}</div>
        </div>
      </main>
    </div>
  );
};

export default OwnerDashboard;
